/**
 * @fileoverview Service for Reddit Connect API operations.
 * @module reddit-connect/services/RedditConnectService
 */

import { getAuth } from 'firebase/auth';
import type {
  RedditConnectionStatus,
  RedditDisconnectResponse,
  RedditOAuthUrlResponse,
  RedditPreparePublishResponse,
  RedditSyncOwnPostsResponse,
} from '../types';

function getApiUrl(): string {
  if (import.meta.env.VITE_AGENT_API_URL) {
    return import.meta.env.VITE_AGENT_API_URL;
  }
  if (import.meta.env.VITE_BUILDER_API_URL) {
    return import.meta.env.VITE_BUILDER_API_URL;
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8081';
  }
  if (hostname.includes('devsegmentlyai')) {
    return 'https://builder-service-519070724517.us-central1.run.app';
  }
  return 'https://api.segmently.ai';
}

export class RedditConnectService {
  private readonly apiUrl: string;

  constructor(apiUrl?: string) {
    this.apiUrl = apiUrl || getApiUrl();
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async getOAuthUrl(projectId: string, returnTo?: string): Promise<string> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ projectId });
    if (returnTo) {
      params.set('returnTo', returnTo);
    }
    const response = await fetch(`${this.apiUrl}/api/reddit/oauth/url?${params}`, {
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to get Reddit OAuth URL' }));
      throw new Error(error.error || 'Failed to get Reddit OAuth URL');
    }

    const data = await response.json() as RedditOAuthUrlResponse;
    return data.url;
  }

  async getStatus(projectId: string, signal?: AbortSignal): Promise<RedditConnectionStatus> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ projectId });
    const response = await fetch(`${this.apiUrl}/api/reddit/status?${params}`, {
      headers,
      signal,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to get Reddit connection status' }));
      throw new Error(error.error || 'Failed to get Reddit connection status');
    }

    return response.json();
  }

  async disconnect(projectId: string): Promise<RedditDisconnectResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/reddit/disconnect`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ projectId }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to disconnect Reddit account' }));
      throw new Error(error.error || 'Failed to disconnect Reddit account');
    }

    return response.json();
  }

  async syncOwnPosts(request: {
    projectId: string;
    authorId?: string;
    maxResults?: number | 'all';
    after?: string;
  }): Promise<RedditSyncOwnPostsResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/reddit/sync-own-posts`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to sync Reddit posts' }));
      throw new Error(error.error || 'Failed to sync Reddit posts');
    }

    return response.json();
  }

  async preparePublish(request: {
    projectId: string;
    subreddit: string;
    kind: 'self' | 'link';
    title: string;
    text?: string;
    url?: string;
    sendReplies?: boolean;
  }): Promise<RedditPreparePublishResponse> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/reddit/publish/prepare`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to prepare Reddit publish' }));
      throw new Error(error.error || 'Failed to prepare Reddit publish');
    }

    return response.json();
  }
}

let singleton: RedditConnectService | null = null;

export function getRedditConnectService(): RedditConnectService {
  if (!singleton) {
    singleton = new RedditConnectService();
  }
  return singleton;
}

export function createRedditConnectService(apiUrl?: string): RedditConnectService {
  return new RedditConnectService(apiUrl);
}

export default RedditConnectService;

